import { StateAction } from "../../../../../../../helpers/functions/types";
import { InputFieldProps, RenderFieldProps } from "../types";
import arePlainObjectsEqual, {
  isEmpty,
} from "../../../../../../../helpers/object/arePlainObjectsEqual";
import { TextLikeEntryElement } from "../../../../../../types";
import { parseNumberOrZero } from "../../../../../../../helpers";

type EntityKeyValue<T> = { [K in keyof Partial<T>]: T[K] };

// Builds the row handlers and render data for a FieldsGroupWrapper whose draft entries live in `setFields` state.
export function getFieldsProps<T>(
  setFields: StateAction<T[]>,
  emptyField: T,
  requiredNonEmptyPropsKeys: (keyof T)[] = [],
  disabled?: boolean,
) {
  // Removes the entry at the given row index.
  const handleRemove = (fieldIndex: number) =>
    setFields((prevFields) =>
      prevFields.filter((_, index) => index !== fieldIndex),
    );

  // Prepends `emptyField`, so a new entry appears at the top of the list.
  const handleAdd = () =>
    setFields((prevFields) => [emptyField, ...prevFields]);

  const handleChange = (updateData: Partial<T>, fieldIndex: number) => {
    setFields((fields) => {
      return fields.map((field, index) =>
        index !== fieldIndex
          ? field
          : {
              ...field,
              ...updateData,
            },
      );
    });
  };

  const fieldCommonProps =
    (field: T, fieldNumber: number) => (fieldKey: keyof T) => {
      const stringKey = String(fieldKey);
      const stringId = `${stringKey}${fieldNumber}`;
      return {
        value: field[fieldKey] ?? "",
        onChange: (target: TextLikeEntryElement) =>
          handleChange({ [fieldKey]: target.value } as Partial<T>, fieldNumber),
        customId: stringId,
        disabled,
        key: fieldKey,
      } as InputFieldProps;
    };

  // Maps every entry to its FieldsGroupWrapper `fieldRender` row, one input per fieldRenderProps item.
  const fieldsRenderData = (
    fields: EntityKeyValue<T>[],
    fieldRenderProps: RenderFieldProps<keyof T>[],
  ) =>
    fields.map((field, fieldIndex) => {
      const fieldProps = fieldCommonProps(field, fieldIndex);
      return fieldRenderProps.map(({ key, ...props }) => {
        // No native `required`: a blank row must not block submit (it's dropped on save),
        // and half-filled rows are flagged by FieldsGroupWrapper instead.
        const merged = { ...fieldProps(key), ...props };
        // Override onChange for number fields to prevent NaN from reaching state
        if (merged.type === "number") {
          merged.onChange = (target: TextLikeEntryElement) =>
            handleChange(
              { [key]: parseNumberOrZero(target.value) } as Partial<T>,
              fieldIndex,
            );
        }
        return merged;
      });
    });

  // Drops entries with an empty requiredNonEmptyPropsKeys value, e.g. blank rows before saving.
  const filterOutEmptyFields = (
    fields: EntityKeyValue<T>[],
  ): EntityKeyValue<T>[] =>
    fields.filter((field) =>
      requiredNonEmptyPropsKeys.every((key) => !isEmpty(field[key])),
    );

  // Compares saved and draft entries, ignoring empty ones; used to disable submit when nothing changed.
  const areSameFields = (
    oldFields: EntityKeyValue<T>[],
    newFields: EntityKeyValue<T>[],
  ) => {
    const relevantOldFields = filterOutEmptyFields(oldFields);
    const relevantNewFields = filterOutEmptyFields(newFields);
    if (relevantOldFields.length !== relevantNewFields.length) return false;
    if (relevantNewFields.length === 0) return relevantOldFields.length === 0;
    const fieldKeys = Object.keys(relevantNewFields[0]) as (keyof T)[];
    return relevantOldFields.reduce(
      (acc, oldField, index) =>
        acc &&
        arePlainObjectsEqual<T>(
          oldField,
          relevantNewFields[index],
          fieldKeys,
          true, //todo - adapt for different boosting types in scope of 39064
        ),
      true,
    );
  };

  return {
    handleRemove,
    handleAdd,
    handleChange,
    fieldsRenderData,
    filterOutEmptyFields,
    areSameFields,
  };
}
